/**
 * Service tokens — CI/CD-scoped bearer credentials for the plugin endpoints.
 *
 * Secrets are only returned once (on issue or rotate) — we show them in a
 * one-shot modal that the operator must copy before dismissing.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { ApiError } from '@optum/api-client/client';
import {
  deleteServiceToken,
  listServiceTokens,
  rotateServiceToken,
} from '@optum/api-client/endpoints/service-tokens';
import type { ServiceTokenIssue, ServiceTokenSummary } from '@optum/api-client/types';

import { Button } from '../../ui/Button.js';
import { CredentialTable, type CredentialRow } from '../../ui/CredentialTable.js';
import { EmptyBlock, ErrorBlock, LoadingBlock } from '../../ui/AsyncState.js';
import { useToast } from '../../ui/useToast.js';
import { expiryStatus, inOverlap } from '../../lib/token-expiry.js';
import { qk } from '../../lib/query-keys.js';
import { useStepUp } from '../../lib/step-up.js';
import { useRecordPanel } from '../../components/shell/record.js';
import { SectionHeader } from '../../ui/SectionHeader.js';
import { TokenRevealDialog } from './TokenRevealDialog.js';

export function ServiceTokensPage() {
  const panel = useRecordPanel();
  const query = useQuery({ queryKey: qk.serviceTokens(), queryFn: listServiceTokens });
  const [issued, setIssued] = useState<ServiceTokenIssue | null>(null);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <SectionHeader
        title="Service tokens"
        subtitle="Bearer credentials for CI/CD workflows. Scope is always plugins."
        actions={
          <Button variant="primary" icon="add" onClick={() => panel.openForm({ name: 'issue-token' })}>
            Issue token
          </Button>
        }
      />
      <>
        {query.isPending && <LoadingBlock />}
        {query.isError && (
          <ErrorBlock
            message={query.error instanceof Error ? query.error.message : 'Failed to load'}
          />
        )}
        {query.data?.length === 0 && <EmptyBlock>No service tokens yet.</EmptyBlock>}
        {query.data && query.data.length > 0 && (
          <CredentialTable
            selectedKey={panel.current?.kind === 'service-token' ? panel.current.id : undefined}
            onRowClick={(row) => panel.open({ kind: 'service-token', id: row.id })}
            rows={query.data.map<CredentialRow>((t) => ({
              id: t.id,
              name: t.label,
              scope: t.scope,
              // Two secrets working at once is its own state, and the operator
              // needs to see it end — so it sits beside the deadline rather
              // than replacing it.
              ...(inOverlap(t.previous_retires_at)
                ? { sub: 'previous secret still accepted' }
                : {}),
              status: (() => {
                const { label, tone } = expiryStatus(t.expires_at);
                return { label, tone };
              })(),
              actions: <TokenActions token={t} onRotated={setIssued} />,
            }))}
            count={`${query.data.length} token${query.data.length === 1 ? '' : 's'}`}
            hint="secrets reveal once — copy before closing"
          />
        )}
      </>
      <TokenRevealDialog secret={issued} onClose={() => setIssued(null)} />
    </div>
  );
}

function TokenActions({
  token,
  onRotated,
}: {
  token: ServiceTokenSummary;
  onRotated: (t: ServiceTokenIssue) => void;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  // A rotation returns the new secret, so the agent asks for a fresh sign-in.
  const [guardedRotate, stepUpDialog] = useStepUp(rotateServiceToken);
  const rotate = useMutation({
    mutationFn: () => guardedRotate(token.id),
    onSuccess: (t) => {
      qc.invalidateQueries({ queryKey: qk.serviceTokens() });
      onRotated(t);
    },
    onError: (e) => toast.error('Rotate failed', e instanceof ApiError ? e.message : String(e)),
  });
  const remove = useMutation({
    mutationFn: () => deleteServiceToken(token.id),
    onSuccess: () => {
      toast.success('Deleted');
      qc.invalidateQueries({ queryKey: qk.serviceTokens() });
    },
    onError: (e) => toast.error('Delete failed', e instanceof ApiError ? e.message : String(e)),
  });

  return (
    <>
      {stepUpDialog}
      <Button
        size="sm"
        variant="secondary"
        onClick={() => rotate.mutate()}
        disabled={rotate.isPending}
      >
        Rotate
      </Button>
      <Button
        size="sm"
        variant="danger"
        onClick={() => remove.mutate()}
        disabled={remove.isPending}
      >
        Delete
      </Button>
    </>
  );
}

